import axios from 'axios';

const API_BASE_URL = 'https://fastline.com-eg.net';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json, text/plain, */*',
  },
  timeout: 30000,
});

export const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Request Interceptor: Attach Auth Token and Idempotency Key for financial mutations
apiClient.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    const isAuthRoute = url.includes('/api/v1/auth/login') || url.includes('/api/v1/auth/refresh');

    if (!isAuthRoute) {
      const token = localStorage.getItem('fastline_access_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Attach Idempotency-Key for mutating finance requests if not already set
    const method = (config.method || '').toLowerCase();
    if ((method === 'post' || method === 'put') && url.includes('/api/v1/finance/')) {
      if (!config.headers['Idempotency-Key']) {
        config.headers['Idempotency-Key'] = generateUUID();
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle 401 Unauthorized Refresh Queue
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/api/v1/auth/login') || originalRequest.url?.includes('/api/v1/auth/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('fastline_refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        localStorage.removeItem('fastline_access_token');
        localStorage.removeItem('fastline_refresh_token');
        localStorage.removeItem('fastline_user');
        window.location.href = '/';
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
          refreshToken: refreshToken,
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data;
        localStorage.setItem('fastline_access_token', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('fastline_refresh_token', newRefreshToken);
        }

        apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        processQueue(null, accessToken);
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        localStorage.removeItem('fastline_access_token');
        localStorage.removeItem('fastline_refresh_token');
        localStorage.removeItem('fastline_user');
        window.location.href = '/';
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
